/**
 * Confirmation Popup Manager
 * Utility to show confirmation popups and wait for user response
 */
import OrbieeFlow from "../components/ChartBot/OrbieeFlow";


let popupState = {
  isOpen: false,
  resolver: null,
  title: "Confirmation",
  message: "Please confirm your choice",
};

let subscribers = [];

/**
 * Subscribe to popup state changes
 * @param {Function} callback - Callback function that receives popup state
 * @returns {Function} Unsubscribe function
 */
export function subscribe(callback) {
  subscribers.push(callback);
  // Immediately call with current state
  callback(popupState);

  // Return unsubscribe function
  return () => {
    subscribers = subscribers.filter((sub) => sub !== callback);
  };
}

/**
 * Notify all subscribers of state change
 */
function notifySubscribers() {
  subscribers.forEach((callback) => callback(popupState));
}

/**
 * Show confirmation popup and wait for user response
 * @param {Object} options - Popup options
 * @param {string} options.title - Popup title
 * @param {string} options.message - Popup message
 * @returns {Promise<string>} Promise that resolves with 'yes', 'no', or 'cancel'
 */
export async function showConfirmationPopup({
  title = "Confirmation",
  message = "Please confirm your choice",
  customerId,
  navigate,
  addToCart,
  onLastResponse = null,
} = {}) {
  const answer = await new Promise((resolve) => {
    // If there's already a popup open, cancel it first
    if (popupState.isOpen && popupState.resolver) {
      popupState.resolver("cancel");
    }

    popupState = {
      isOpen: true,
      resolver: resolve,
      title,
      message,
    };

    notifySubscribers();
  });

  // The shopper's answer is itself a turn. Awaited so a rejection is handled
  // here rather than surfacing as an unhandled promise rejection.
  try {
    return await OrbieeFlow(
      { content: answer, transcription: answer },
      customerId,
      navigate,
      addToCart,
      "eng",
      onLastResponse
    );
  } catch (err) {
    console.error("[confirmationPopup] follow-up turn failed", err);
    return null;
  }
}

/**
 * Handle user selection and close popup
 * @param {string} result - User's choice: 'yes', 'no', or 'cancel'
 */
export function handlePopupResult(result) {
  if (popupState.resolver) {
    popupState.resolver(result);
  }

  popupState = {
    isOpen: false,
    resolver: null,
    title: "Confirmation",
    message: "Please confirm your choice",
  };

  notifySubscribers();
}

/**
 * Get current popup state
 * @returns {Object} Current popup state
 */
export function getPopupState() {
  return { ...popupState };
}

/**
 * Close popup without result (equivalent to cancel)
 */
export function closePopup() {
  handlePopupResult("cancel");
}

